/**
 * TruePath Analytics
 *
 * Three layers:
 *  1. PostHog  — product analytics (funnels, session recordings, feature flags)
 *  2. Sentry   — error monitoring + performance tracing
 *  3. GA4      — web traffic / SEO (injected via index.html script tag)
 *
 * Required env vars (set in Vercel dashboard + local .env):
 *   VITE_POSTHOG_KEY      — from app.posthog.com > Project Settings > API Key
 *   VITE_POSTHOG_HOST     — https://app.posthog.com  (or your EU endpoint)
 *   VITE_SENTRY_DSN       — from sentry.io > Project > Settings > Client Keys
 */

import posthog from 'posthog-js';

// ─── PostHog ──────────────────────────────────────────────────────────────────

export const initPostHog = () => {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return;
  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
    capture_pageview: true,
    capture_pageleave: true,
    session_recording: {
      maskAllInputs: true, // FERPA safety — mask all form inputs in recordings
    },
    loaded: (ph) => {
      if (import.meta.env.DEV) ph.opt_out_capturing(); // no tracking in dev
    },
  });
};

// ─── Event tracking ───────────────────────────────────────────────────────────

export type TrackEvent =
  | { event: 'edition_selected';       props: { edition: string } }
  | { event: 'assessment_started';     props: { edition: string } }
  | { event: 'assessment_step';        props: { edition: string; step: number; screen_id: string } }
  | { event: 'assessment_completed';   props: { edition: string; total_steps: number; used_fallback: boolean } }
  | { event: 'results_tab_viewed';     props: { tab: string } }
  | { event: 'path_viewed';            props: { path_name: string; risk_level: string } }
  | { event: 'blueprint_shared';       props: { share_id: string } }
  | { event: 'blueprint_exported_pdf'; props: { edition: string } }
  | { event: 'sign_up';                props: { method: 'email' } }
  | { event: 'sign_in';                props: { method: 'email' } }
  | { event: 'sign_out';               props: Record<string, never> }
  | { event: 'dashboard_viewed';       props: { assessment_count: number } }
  | { event: 'school_catalog_loaded';  props: { school_name: string; source: 'scrape' | 'admin_upload' } };

export const track = ({ event, props }: TrackEvent) => {
  if (typeof posthog !== 'undefined') {
    posthog.capture(event, props);
  }
};

export const identifyUser = (userId: string, traits?: Record<string, unknown>) => {
  if (typeof posthog !== 'undefined') {
    posthog.identify(userId, traits);
  }
};

export const resetUser = () => {
  if (typeof posthog !== 'undefined') {
    posthog.reset();
  }
};
